import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Collapse, Empty, InputNumber, Tag, Typography } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";

import { useAntdTheme } from "../../shared/hooks";
import { tierLabel } from "./components";
import {
  ALL_TIERS,
  NegativePriceError,
  OfferedModificationSchema,
  OverrideInputs,
  dollarsToCents,
  formatAdjustment,
  formatCents,
  overrideKey,
} from "./pricing";
import { ComboAddonTier } from "./types";

const { Text } = Typography;

type OptionPricingProps = {
  schemas: OfferedModificationSchema[];
  tiers: ReadonlyArray<ComboAddonTier>;
  inputs: OverrideInputs;
  errors: NegativePriceError[];
  onChange: (key: string, value: string | null) => void;
  onReset: (choiceId: string) => void;
};

/**
 * "Option pricing" section of the add-on editor: per modification choice used
 * by the add-on's items, an add-on-wide override and expandable per-tier
 * overrides. Blank means "inherit" (per tier: the all-tiers override, else
 * the regular adjustment) and isn't saved.
 */
export const OptionPricing: React.FC<OptionPricingProps> = ({
  schemas,
  tiers,
  inputs,
  errors,
  onChange,
  onReset,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const overriddenChoiceIds = new Set(Object.keys(inputs).map((key) => key.split("|")[1]));
  const overrideCount = (s: OfferedModificationSchema) =>
    s.choices.filter((c) => overriddenChoiceIds.has(c.choice.id)).length;
  // Schemas the admin collapsed. When an existing add-on with many option
  // schemas opens, those without overrides start collapsed; schemas that
  // appear later (items added) start open, and ones with errors always are.
  const [collapsed, setCollapsed] = React.useState<Set<string>>(
    () =>
      new Set(
        schemas.length <= COLLAPSE_THRESHOLD
          ? []
          : schemas.filter((s) => overrideCount(s) === 0).map((s) => s.schema.id)
      )
  );
  const hasError = (s: OfferedModificationSchema) =>
    s.choices.some((c) => errors.some((e) => e.choiceId === c.choice.id));
  const activeKeys = schemas
    .filter((s) => !collapsed.has(s.schema.id) || hasError(s))
    .map((s) => s.schema.id);

  if (schemas.length === 0) {
    return (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("combo_no_options_to_price")} />
    );
  }

  const tierById = new Map(tiers.map((tier) => [tier.id, tier]));

  const toggle = (choiceId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(choiceId)) {
        next.delete(choiceId);
      } else {
        next.add(choiceId);
      }
      return next;
    });

  const input = (key: string, placeholder: string, invalid: boolean, label: string) => (
    <InputNumber<string>
      aria-label={label}
      prefix="$"
      step="0.01"
      precision={2}
      stringMode
      value={inputs[key] ?? null}
      placeholder={placeholder}
      size="small"
      status={invalid ? "error" : undefined}
      onChange={(v) => onChange(key, v)}
      style={{ width: 150 }}
    />
  );

  return (
    <Collapse
      activeKey={activeKeys}
      onChange={(keys) => {
        const open = new Set(Array.isArray(keys) ? keys : [keys]);
        setCollapsed(new Set(schemas.map((s) => s.schema.id).filter((id) => !open.has(id))));
      }}
      items={schemas.map((offered) => {
        const { schema, choices } = offered;
        const count = overrideCount(offered);
        return {
          key: schema.id,
          label: (
            <span data-testid="combo-option-schema">
              <Text strong>{schema.description}</Text>
              {count > 0 ? (
                <Tag color="blue" style={{ marginLeft: token.marginXS }}>
                  {t("combo_overridden_count", { count })}
                </Tag>
              ) : null}
            </span>
          ),
          children: choices.map(({ choice, tierIds }) => {
            const allKey = overrideKey(ALL_TIERS, choice.id);
            const allCents = dollarsToCents(inputs[allKey]);
            const choiceErrors = errors.filter((e) => e.choiceId === choice.id);
            const allTierErrors = choiceErrors.filter((e) => e.source === "all_tiers");
            const hasTierOverride = tierIds.some(
              (tierId) => dollarsToCents(inputs[overrideKey(tierId, choice.id)]) !== null
            );
            const isOpen = expanded.has(choice.id) || choiceErrors.some((e) => e.source === "tier");
            const inherited = allCents ?? choice.price_adjustment;

            return (
              <div
                key={choice.id}
                data-testid={`combo-option-${choice.description}`}
                style={{
                  padding: `${token.paddingXS}px 0`,
                  borderBottom: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: token.marginSM,
                  }}
                >
                  <Text strong style={{ minWidth: 140 }}>
                    {choice.description}
                  </Text>
                  <Text type="secondary" style={{ minWidth: 130 }}>
                    {t("combo_option_regular", {
                      price: formatAdjustment(choice.price_adjustment),
                    })}
                  </Text>
                  <Text>{t("combo_all_tiers")}</Text>
                  {input(
                    allKey,
                    formatAdjustment(choice.price_adjustment),
                    allTierErrors.length > 0,
                    t("combo_all_tiers_override_for", { name: choice.description })
                  )}
                  <Button
                    type="link"
                    size="small"
                    icon={isOpen ? <DownOutlined /> : <RightOutlined />}
                    onClick={() => toggle(choice.id)}
                  >
                    {t("combo_per_tier", { count: tierIds.length })}
                    {hasTierOverride ? " *" : ""}
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    disabled={allCents === null && !hasTierOverride}
                    onClick={() => onReset(choice.id)}
                  >
                    {t("reset")}
                  </Button>
                </div>
                {allTierErrors.length > 0 ? (
                  <Text type="danger" style={{ display: "block", fontSize: token.fontSizeSM }}>
                    {t("combo_option_negative_tiers", {
                      tiers: allTierErrors
                        .map((e) => {
                          const tier = tierById.get(e.tierId);
                          return tier ? tierLabel(t, tier) : e.tierId;
                        })
                        .join(", "),
                    })}
                  </Text>
                ) : null}
                {isOpen ? (
                  <div style={{ paddingLeft: token.paddingXL, marginTop: token.marginXS }}>
                    {tierIds.map((tierId) => {
                      const tier = tierById.get(tierId);
                      if (!tier) {
                        return null;
                      }
                      const key = overrideKey(tierId, choice.id);
                      const tierError = choiceErrors.find(
                        (e) => e.tierId === tierId && e.source === "tier"
                      );
                      return (
                        <div
                          key={key}
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: token.marginSM,
                            marginBottom: token.marginXXS,
                          }}
                        >
                          <Text style={{ minWidth: 140 }}>{tierLabel(t, tier)}</Text>
                          {input(
                            key,
                            t("combo_inherits", { price: formatAdjustment(inherited) }),
                            !!tierError,
                            t("combo_tier_override_for", {
                              name: choice.description,
                              tier: tierLabel(t, tier),
                            })
                          )}
                          {tierError ? (
                            <Text type="danger" style={{ fontSize: token.fontSizeSM }}>
                              {t("combo_option_negative", {
                                price: formatCents(tierError.tierPrice + tierError.adjustment),
                              })}
                            </Text>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          }),
        };
      })}
    />
  );
};

// With at most this many option schemas, all start expanded.
const COLLAPSE_THRESHOLD = 2;
